import { DayOfWeekStartingSunday } from '~/components/v2/forms/HookFormDayOfWeek'

import { ScheduleFrequency, ScheduleInterval } from '~/common/scheduledJobConstants'
import { ConditionGroupData } from '~/custom_reports/v3/state/StagingStateProvider'

export { ScheduleFrequency, ScheduleInterval }

export interface DatasetFieldFilterOption {
  path: string
  name: string
  selected: boolean
}

export type VersionQueryItemType = { [key: string]: any }

export interface Option<T> {
  label: string
  value: T
}

export interface ReportQueryType {
  joins: VersionQueryItemType[]
  fields: VersionQueryItemType[]
  dataset?: string
  filters: VersionQueryItemType[]
  orderBy: VersionQueryItemType[]
  parameterFields: VersionQueryItemType[]
  calculatedFields: VersionQueryItemType[]
  fieldSources: any
}

export interface VersionChangesType {
  display_name: string
  field_name: string
  before: string | VersionQueryItemType[]
  after: string | VersionQueryItemType[]
}

export interface SavedReport extends Report {
  query: ReportQuery
  validate_accessible_query: boolean
  is_favorite: boolean
  owned_by_user: boolean
}

interface DefaultReport {
  id: number
  name: string
  description: string
  section_name: string
  report_type: string
  config_export_enabled: boolean
  export_hyperlink_enabled: boolean
  api_usage_enabled: boolean
  domain: string
}

export type ReportOwnerType = {
  id: number
  name?: string
  type: 'User' | 'Role' | 'RoleTag'
}

export type ReportOwnerSubmitType = {
  id: number
  key?: string
  label?: string
  type: 'User' | 'Role' | 'RoleTag'
}

export type ShareSettingsType = {
  first_shared_at: string
  is_shared_by_me: boolean
  shared_with: string[]
  shared_by: string
  is_shared_with_me: boolean
}

export type LinkedUsages = {
  api_integration: boolean
  sftp_integration: boolean
  email_integration: boolean
  invoices: { id: string; name: string; permission: boolean }[]
}

export interface Report extends DefaultReport {
  uuid: string
  owner: ReportOwnerType
  created_by: number
  creator_name: string
  created_at: string
  updated_at: string
  share_settings: ShareSettingsType
  linked_usages: LinkedUsages
  belongs_to_merge_report: boolean
  selected_version: number
  current_version_name: string
}

export interface VersionType extends DefaultReport {
  query: ReportQueryType[]
  updated_at: string
  updated_by: number
  updated_by_name: string
  saved_report: number
  url: string
  prev_version: number
  changes: VersionChangesType[]
  is_revert: boolean
}

export interface DatasetField extends ReportField {
  aggregations?: string[]
  format_options?: Array<ReportDataFormats>
  filter_aggregations?: any
  filter_allowed?: boolean
  filter_metadata?: any
  format_metadata?: Array<string>
  filter_type?: string
  permissions?: string
  type: ReportDataTypes
}

export interface FormatOption {
  id: string
  name: string
}

export interface Dataset {
  fields: DatasetField[]
  id: string
  name: string
  relations: string[]
  show_in_list: boolean
  l10n_name_key?: string | null
  l10n_name_context?: Record<string, any> | null
}

export enum RelationCardinality {
  OneToOne = 'one_to_one',
  ManyToOne = 'many_to_one',
  OneToMany = 'one_to_many'
}
export interface DatasetRelation extends JoinOption {
  level: number
  cardinality: RelationCardinality
  always_allowed_paths: string[]
  restricted_paths: string[]
  is_enabled: boolean
}

interface ReportConfigDateSetting {
  default_format: string
}

interface ReportConfigExportDefault {
  export_file_extension: string
  export_format: string
}

interface ReportConfigExportSetting extends ReportConfigExportDefault {
  include_column_headers: boolean
  include_report_heading: boolean
}

interface ReportConfigSharingExportSetting extends ReportConfigExportDefault {
  include_header: boolean
}

interface ReportConfigSharingSetting {
  allow_report_sharing_with_vendors: boolean
  exporting: ReportConfigSharingExportSetting
  report_sharing_role_tags: number[]
}

export interface ReportConfigSettings {
  date_formats: ReportConfigDateSetting
  exporting: ReportConfigExportSetting
  sharing: ReportConfigSharingSetting
}

export interface ReportDefault {
  name: string
  description: string
  ownerId: number
  section: string
  configExport: boolean
  reportType: ReportType
  exportHyperlink: boolean
  apiUsage: boolean
  creatorName?: string
}

export interface ReportDetails extends ReportDefault {
  owner: ReportOwnerSubmitType
}

export type FormatConfig = Partial<Record<ReportDataFormats, unknown>>
export type FieldFormatConfig = Record<string, FormatConfig>
export type FieldAggregateConfig = Record<string, string | null>

export interface ReportQuery {
  id?: number
  dataset: string | null
  joins: Array<Join>
  fields: Array<Field>
  filters: Array<QueryFilter>
  groupFields: Array<GroupField>
  orderBy: Array<SortDirection>
  parameterFields: Array<ParameterField>
  calculatedFields: Array<CalculatedField>
}

export type UnionMergeQuery = {
  report_type_key: 'union_merge'
  // report uuid strings (the backend resolves source reports by uuid)
  source_report_ids: string[]
  union_merge_spec: {
    branch_merge_instruction: 'block' | 'interleave'
    partition_by: Array<{
      direction: 'asc' | 'desc'
      field_id_by_report_id: Record<string, string>
    }>
  }
  // column-label source (subset of source_report_ids); distinct from the section reports below
  header_report_ids?: string[]
  // report whose rows are pinned above the content section
  header_section_report_id?: string
  // report whose rows are pinned below the content section
  footer_section_report_id?: string
}

export function isUnionMergeQuery(query: unknown): query is UnionMergeQuery {
  return (
    typeof query === 'object' && query !== null && (query as Record<string, unknown>).report_type_key === 'union_merge'
  )
}

export interface ReportStateValues {
  query: ReportQueryType
  report: {
    id: number
    uuid: string
    name: string
    report_type: string
    description: string
    section_name: string
    creator_name: string
    owner: ReportOwnerSubmitType
    created_by: CreatedBy
    created_at: string
    config_export_enabled: boolean
    share_settings: ShareSettingsType
    selected_version: number
    current_version_name: string
    is_favorite: boolean
    owned_by_user: boolean
    export_hyperlink_enabled: boolean
    domain: string
  }
}

export interface CreatedBy {
  value: number
  label?: string
}

export enum ReportType {
  Saved = 'saved',
  Base = 'base',
  Standard = 'standard'
}
export type ReportTypeChoices = 'base' | 'saved' | 'standard'
export const ReportTypeMap: { [reportTypeStr: string]: ReportType } = {
  saved: ReportType.Saved,
  base: ReportType.Base,
  standard: ReportType.Standard
}

type ReportTabType = {
  title: string
  path: string
  datatestid: string
}

export interface ReportPageTabsPropsType {
  tabIndex: number
  navTabs: ReportTabType[]
}

export interface Field {
  id: string
  path: string
  field: string
  aggregation?: string | null
  custom_name?: string
  format?: FormatConfig
  target?: string
}

export enum CustomFieldPath {
  Calculated = '__calculated__',
  Parameter = '__parameter__',
  Value = '__value__',
  SystemVariable = '__system__',
  SpecifiedValue = '__specified_value__'
}

export enum FieldTypes {
  ALL = 'all',
  DATASET = 'dataset',
  CALCULATED = 'calculated',
  PARAMETER = 'parameter',
  SYSTEM = 'system',
  VALUE = 'value'
}

export interface ParameterField extends ReportField {
  parameter_default: string
}
export interface GroupField {
  id: string
  name: string
}
export interface FieldSource {
  id: string
  type: string
  dataset_id: string
  path: string
  display_name: string
  relation_name?: string
  target?: string
  source?: string
  cardinality?: RelationCardinality
  level?: number
  always_allowed_paths?: string[]
  restricted_paths?: string[]
  is_enabled?: boolean
}

export interface Join {
  name: string
  id: string
  source: string
  target: string
  path: string
}

export interface JoinOption extends Join {
  description: string
  display_name: string
}

export enum SortDirectionType {
  Ascending = 'asc',
  Descending = 'desc'
}
export interface SortDirection {
  direction: SortDirectionType
  field: string
  path: string
  target?: string
}

export enum ReportDataTypes {
  Integer = 'integer',
  String = 'string',
  Date = 'date',
  Datetime = 'datetime',
  Boolean = 'boolean',
  Html = 'html',
  Array = 'array',
  Json = 'json',
  Json_Array = 'json_array',
  Number = 'number',
  Time = 'time',
  Text = 'text'
}

export enum ReportDataFormats {
  Currency = 'currency-fmt',
  Number = 'num-fmt',
  Percent = 'percentage-fmt',
  String = 'string',
  Html = 'html',
  Date = 'date-fmt',
  Datetime = 'datetime-fmt',
  DecimalPlaces = 'decimal-fmt'
}

export interface ReportColumnConfig {
  type: ReportDataTypes
  dataset_field_id: string
  link: string | null
  format: ReportDataFormats
  id: string
  dynamic_link: boolean
  name: string
}

export interface ReportQueryRequest extends ReportQueryInfoRequest {
  offset: number
}
export interface ReportQueryInfoRequest {
  id?: number
  report_type: string
  // Optional because union-merge reports have no ReportQuery shape — the backend resolves the
  // spec from `id` alone.
  query?: ReportQuery
  col_format?: 'ui' | 'api'
  format?: 'json'
  versionId?: number
  limit: number
  // Union-merge only: bound each merged source report to a few rows instead of the full result.
  sample?: boolean
  // Union-merge only: how many rows sample mode keeps per source report (default 2, see backend
  // UNION_MERGE_SAMPLE_ROWS_PER_SECTION).
  sample_rows_per_branch?: number
}
export interface ReportQueryResponse {
  data: Record<string, unknown>[]
}
export interface AnalyzeReportQueryResponse {
  is_long_running_report: boolean
  execution_time: number
}
export interface ExecuteReportQueryResponse {
  data: Array<Array<unknown>>
  header_row_indexes?: number[]
}
export interface ReportQueryInfoResponse {
  columns: Array<{
    type: ReportDataTypes
    dataset_field_id: string
    link: string
    format: ReportDataFormats
    id: string
    dynamic_link: boolean
    name: string
  }>
  total_pages: number
  total_count: number
}
export interface BaseCustomReportQueryKeyParams {
  id: number | null
  report_type: ReportType
  offset: number
  limit: number
  queryParams: Partial<ReportQuery>
  orderBy: Array<SortDirection>
  viewFilters: Array<QueryFilter> | null
  formats: Array<Field['format']> | null
  aggregations: Array<Field['aggregation']> | null
}
export interface CustomReportQueryKeyParams extends BaseCustomReportQueryKeyParams {
  versionId: number | null
}
export interface ReportQueryInfoKeyParams {
  id: number | null
  versionId: number | null
  report_type: ReportType
  queryParams: Partial<ReportQuery>
  orderBy: Array<SortDirection>
  viewFilters: Array<QueryFilter> | null
  formats: Array<Field['format']> | null
  limit: number
}

export interface ReportDetailsPayload extends ReportDefault {
  id?: number
  validateAccessibleQuery?: boolean
  owner: ReportOwnerSubmitType
}

export interface ReportFieldsPayload extends ReportDefault {
  id?: number
  validateAccessibleQuery?: boolean
  owner: ReportOwnerSubmitType
  query?: ReportQuery
}

export interface SaveReportPayload extends ReportDefault {
  id?: number
  validateAccessibleQuery?: boolean
  query?: ReportQuery | UnionMergeQuery
  owner: ReportOwnerSubmitType
}

export interface RevertReportPayload {
  reportId: number
  selectedVersion: number
}

export interface DeleteReportPayload {
  reportId: number
}

export interface UpdateReportResponse {
  id: number
  report_type: ReportType
}

export interface RunFormattingPreviewPayload {
  column: ReportColumnConfig
  values: Array<unknown>
  format: FormatConfig
}

export interface RunFormattingPreviewResponse {
  result: Array<unknown>
}

export interface RunFormattingDateOptionsPayload {
  column: ReportColumnConfig
  values: Array<unknown>
  formats: Array<{ id: ReportDataFormats; config: { type: string; code: string } }>
}

export interface RunFormattingDateOptionsResponse {
  result: Array<{ id: string; name: string }>
}

export interface QueryFilter {
  path: string
  target: string
  field: string
  operator: string | null
  value: string | Array<string> | number | Array<number> | null
  use_parameter: boolean
  isDynamic?: boolean
}

export enum ExportFormat {
  Excel = 'xls',
  Csv = 'csv',
  PipeSeparated = 'txt',
  TabSeparated = 'tsv'
}
export interface ExportDetails {
  exportFormat: ExportFormat
  fileExtension: string
}

export interface ScheduleFrequencyDetails {
  timezone: string
}

export interface SingleTimeScheduleFrequencyDetails extends ScheduleFrequencyDetails {
  scheduledTime: string
}

export interface TimeRangeScheduleFrequencyDetails extends ScheduleFrequencyDetails {
  scheduledStartTime: string
  scheduledEndTime: string
}

export interface HourlyScheduleFrequencyDetails extends TimeRangeScheduleFrequencyDetails {
  time: string
  repeatHour: number
  repeatMinute: number
}

export type DailyScheduleFrequencyDetails = SingleTimeScheduleFrequencyDetails

export interface WeeklyScheduleFrequencyDetails extends SingleTimeScheduleFrequencyDetails {
  repeatWeek: number
  daysOfTheWeek: Array<DayOfWeekStartingSunday>
}

export enum MonthlyRepeatCriteria {
  SpecificDays,
  NthWeekday,
  LastWeekday,
  LastDay
}

export interface MonthlyScheduleFrequencyDetails extends SingleTimeScheduleFrequencyDetails {
  repeatCriteria: MonthlyRepeatCriteria
  daysOfTheMonth?: Array<number>
  specificDays?: Array<number>
  weekday?: DayOfWeekStartingSunday
  nthWeekday: string
  repeatMonth: number
}

export interface ScheduleDetailsForm<T extends ScheduleFrequencyDetails = ScheduleFrequencyDetails> {
  frequency: ScheduleFrequency
  frequencyDetails: T
  startDate: string
  endDate?: string
  scheduleOwnerId: number
}

export interface FileOptionsForm {
  sendReportWithNoData: boolean
  includeReportHeading: boolean
  includeColumnHeaders: boolean
  exportDetails: ExportDetails
  remoteFileName: string | null
  addTimestampToRemoteFile: boolean
}

export interface EmailReportDeliveryDetails {
  emailRecipients: Array<string>
  userRecipients: Array<string | number>
  includeReportAsAttachment: boolean
}

export interface SftpReportDeliveryDetails {
  sftpDestination: number
  remoteFileName?: string | null
  remoteFolderPath?: string | null
  emailRecipientsForFailure: Array<string>
  userRecipientsForFailure: Array<string | number>
  addTimestampToRemoteFile: boolean
}

export interface ReportDelivery {
  emailDelivery: boolean
  emailDeliveryDetails?: EmailReportDeliveryDetails

  sftpDelivery: boolean
  sftpDeliveryDetails?: SftpReportDeliveryDetails
}

export interface SaveSchedulePayload {
  id?: number
  // type here is intentionally left as 'custom_report' because this not the report type. this is the schedule type
  type: 'custom_report'
  reference_id: string
  schedule_type: string
  frequency: ScheduleFrequency
  start_datetime: string
  end_datetime?: string
  scheduled_time?: string
  scheduled_end_time?: string
  days_of_month?: Array<number>
  days_of_week?: Array<DayOfWeekStartingSunday>
  enabled: boolean
  metadata: ReportScheduleMetadata
  interval?: ScheduleInterval
  interval_length?: number
  hour?: number
  minute?: number
  schedule_owner_id: number
}
export interface SaveScheduleResponse {
  id: number
}

export interface ScheduledReportPayload {
  scheduleId: number
}

export interface ReportScheduleMetadata {
  timezone: string
  report_type: ReportType
  export_format: ExportFormat
  sftp_delivery: boolean
  email_delivery: boolean
  email_recipients: Array<string>
  user_recipients: Array<string | number>
  include_report_as_attachment: boolean
  sftp_failure_recipients: Array<string>
  sftp_failure_user_recipients: Array<string | number>
  static_remote_file: string | null
  export_file_extension: string
  sftp_upload_directory: string | null
  include_column_headers: boolean
  include_report_heading: boolean
  send_report_with_no_data: boolean
  sftp_export_definition_id: number | null
  add_timestamp_to_static_remote_file: boolean
  repeat_criteria: MonthlyRepeatCriteria | null
  nth_weekday: string | null
  weekday: DayOfWeekStartingSunday | null
  repeat_week: number | null
  repeat_month: number | null
  repeat_hour: number | null
  repeat_minute: number | null
}

export interface ReportScheduleResponse {
  id: number
  type: 'custom_report'
  reference_id: string
  frequency: number
  days_of_week: Array<DayOfWeekStartingSunday>
  days_of_month: Array<number>
  months_of_year?: Array<number>
  scheduled_time: string
  scheduled_end_time: string
  metadata: ReportScheduleMetadata
  enabled: boolean
  input_cron_expression: string
  schedule_type: string
  interval: ScheduleInterval
  interval_length: number
  start_datetime: string
  end_datetime: string
  schedule_owner_name: string
  schedule_owner_is_active: boolean
  repeat_criteria: MonthlyRepeatCriteria
  schedule_owner_id: number
}
export type ListReportScheduleResponse = Array<ReportScheduleResponse>

export interface ListSftpOptionsResponse {
  sftp_definitions: Array<SftpOption>
}

export interface SftpOption {
  id: number
  name: string
}

export enum Formula {
  Add = 'Add',
  Age = 'Age',
  ArrayToString = 'ArrayToString',
  Avg = 'Avg',
  Case = 'Case',
  Coalesce = 'Coalesce',
  Concat = 'Concat',
  Count = 'Count',
  DateDiff = 'DateDiff',
  DateTrunc = 'DateTrunc',
  Divide = 'Divide',
  Min = 'Min',
  Max = 'Max',
  Multiply = 'Multiply',
  Round = 'Round',
  PadText = 'PadText',
  Substring = 'Substring',
  SubstringLeft = 'Left',
  SubstringRight = 'Right',
  SubstringMid = 'Substring Mid',
  Subtract = 'Subtract',
  Sum = 'Sum',
  ToString = 'ToString',
  UnNest = 'UnNest',
  Value = 'Value'
}

export enum RoundPrecisionOption {
  OneDp = '1',
  TwoDp = '2',
  ThreeDp = '3',
  NearestWholeNum = '0',
  NearestMultiplesOfTen = '-1',
  NearestMultiplesOfHundred = '-2',
  Other = 'other'
}

export enum CountOption {
  AllValues = 'AllValues',
  DistinctValues = 'DistinctValues'
}

export interface CountSettings {
  countOption: CountOption
}

export enum ConcatenateOption {
  None = 'None',
  Space = 'Space',
  Comma = 'Comma',
  Hyphen = 'Hyphen',
  Other = 'Other'
}

export enum SubstringTypes {
  Middle = 'Middle',
  Right = 'Right',
  Left = 'Left'
}

export enum PaddingTypes {
  Left = 'Left',
  Right = 'Right'
}

export interface PathAndField {
  path: string | null
  field: string | null
  target?: string | null
}

export interface ConditionType {
  or: string | null
  and: string | null
}

export interface CalculatedField extends ReportField {
  fields: Array<PathAndField> | Array<ConditionGroupData>
  formula: Formula
  distinct?: boolean
  separator?: string
  length?: number
  start_index?: number
  substring_type?: SubstringTypes
  padding_type?: PaddingTypes
  format?: string
  value?: string | null
  else_field?: PathAndField
  else_is_field?: boolean
  groupOperation?: string | null
  v3_case?: boolean
  precision?: string
  else?: string | null
}

export interface ReportField {
  id: string
  name: string
  description?: string
  data_type?: ReportDataTypes | null
  l10n_name_key?: string | null
  l10n_name_context?: Record<string, any> | null
  l10n_description_key?: string | null
}

export enum DateTruncPrecision {
  Second = 'second',
  Minute = 'minute',
  Hour = 'hour',
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Quarter = 'quarter',
  Year = 'year',
  Decade = 'decade'
}

export enum ReportAccessRecipientType {
  RoleTag = 'RoleTag',
  Role = 'Role',
  User = 'User'
}

export interface ReportAccessRecipient {
  id?: number | string
  value: number | string
  label: string
  type?: ReportAccessRecipientType
  key?: string
  email?: string
}

export interface RecipientsResponse {
  roles: ReportAccessRecipient[]
  role_tags: ReportAccessRecipient[]
  users: ReportAccessRecipient[]
}

export interface RecipientListItemProps {
  recipient: ReportAccessRecipient
  onRemove?: (recipient: ReportAccessRecipient) => void
}

export interface SystemVarableField extends ReportField {
  description: string
  format_options?: Array<ReportDataFormats>
  format_metadata?: Array<string>
}

export interface AllRelatedFields {
  l10n_name_key: any
  l10n_name_context: any
  section_name?: string
  section_name_key?: string
  section_name_context?: any
  l10n_description_key?: string
  l10n_description_context?: any
  field_type: FieldTypes
  description: string
  path: string
  field: string
  target?: string
  name?: string
}

export type ListSystemVariablesResponse = Array<SystemVarableField>

export type ListAllRelatedFieldsResponse = Array<AllRelatedFields>

export type FilterOptionsResponse = Array<Option<string>>

export enum FilterOperator {
  // Billing Cycle
  EqualBc = 'equal_bc',
  NotEqualBc = 'not_equal_bc',
  GreaterThanBc = 'greater_than_bc',
  GreaterThanOrEqualBc = 'greater_than_or_equal_bc',
  LessThanBc = 'less_than_bc',
  LessThanOrEqualBc = 'less_than_or_equal_bc',
  PreviousBc = 'previous_bc',
  CurrentBc = 'current_bc',
  LastBc = 'last_bc',

  // Base Operators
  Equal = 'equal',
  Contains = 'contains',
  DoesNotContain = 'does_not_contain',
  NotEqual = 'not_equal',
  AnyOf = 'any_of',
  NotAnyOf = 'not_any_of',
  Includes = 'includes',
  IncludesAny = 'includes_any',
  NotIncludes = 'not_includes',
  JsonIncludes = 'json_includes',
  JsonNotIncludes = 'json_not_includes',
  JsonArrayIsEmpty = 'json_array_is_empty',
  JsonArrayNotEmpty = 'json_array_not_empty',
  WithinHierarchy = 'within_hierarchy',
  NotWithinHierarchy = 'not_within_hierarchy',
  IsNull = 'is_null',
  NotNull = 'not_null',
  StartsWith = 'starts_with',
  NotStartsWith = 'not_starts_with',
  GreaterThan = 'greater_than',
  GreaterThanOrEqual = 'greater_than_or_equal',
  LessThan = 'less_than',
  LessThanOrEqual = 'less_than_or_equal',
  Today = 'today',
  Yesterday = 'yesterday',
  Tomorrow = 'tomorrow',
  Last7Days = 'last_7_days',
  Next7Days = 'next_7_days',
  Last2Weeks = 'last_2_weeks',
  Next2Weeks = 'next_2_weeks',
  Last30Days = 'last_30_days',
  Next30Days = 'next_30_days',
  LastMonth = 'last_month',
  NextMonth = 'next_month',
  ThisMonth = 'this_month',
  Last6Months = 'last_6_months',
  Next6Months = 'next_6_months',
  Last12Months = 'last_12_months',
  Next12Months = 'next_12_months'
}

export type ReportSettingsResponse = {
  settings: ReportConfigSettings
  filters: {
    id: string
    title: string
    type: string
    default_is_open: boolean
    options: {
      id: string
      text: string
      subtext: string
      value: string | boolean
    }[]
  }[]
  permissions: {
    canShareReport: boolean
    canExportReport: boolean
    canAuthorReport: {
      full: boolean
      limited?: boolean
    }
    canScheduleSftpReport: boolean
    canScheduleEmailReport: boolean
    canPublishReport: boolean
    canViewMergeReport?: boolean
    canCreateMergeReport?: boolean
    canEditBaseReports: boolean
    canVsyncReport: boolean
    canImportReportDefinitions: boolean
    canExportReportDefinitions: boolean
    /** custom.reports.superuser perm. Note this is not Django superuser, nor isVndlySuperuser. */
    isReportSuperuser: boolean
    /** VNDLY Superuser [staff] RBAC role. Different to isReportSuperuser and Django User.is_superuser. */
    isVndlySuperuser?: boolean
    isReportScheduleAdmin?: boolean
    isReportSettingsAdmin?: boolean
    canShareReportWithVendors: boolean
    canShareReportWithProgramTeams: boolean
    canVendorShareReportRoleTags: boolean
    canAccessCustomReports: boolean
    hasAdminSettings?: boolean
  }
}

export type NamedQueryFilter = {
  id: string
  operator: string
  path?: string
  field?: string
  type?: string
  value?: string | number | boolean | Array<string | number | boolean> | null
}

// Report Files Types
export type ReportFileRecipients = {
  users: string[]
  emails: string[]
}

export type TransferFile = {
  id: number
  file_name: string
  file_type: string
  number_of_records: number | null
  duration: string | null
  file_size: number | null
}

export enum ReportFileExportType {
  Scheduled = 'Scheduled',
  Export = 'Export'
}

export enum ReportFileRecipientType {
  Users = 'users',
  Emails = 'emails'
}

export type ReportFileItem = {
  id: number
  created_at: string
  recipients: ReportFileRecipients
  export_type: ReportFileExportType
  saved_report: {
    id: number
    name: string
    type: string
  } | null
  transfer_file: TransferFile
  scheduled_job_id?: number
  path: string
}

export type RecipientType = ReportFileRecipientType.Users | ReportFileRecipientType.Emails
